// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import { blogTileImageStyles, blogTileReadMoreStyles } from "@store/Themes/common/mixins/blog";
import { linkAltStyles } from "@store/Themes/common/mixins/link";
import { blogProps } from "@store/Themes/common/props/blog";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentLanguage } from "@toolkit/Framework/Store/Context/ContextHooks";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import React, { FC, HTMLAttributes } from "react";
import { css } from "styled-components";

export interface BlogCategoryFeaturedArticlePresentationProps extends ExpandStyles<BlogCategoryFeaturedArticleStyles> {}

export interface BlogCategoryFeaturedArticleProps
    extends BlogCategoryFeaturedArticlePresentationProps,
        HTMLAttributes<HTMLElement> {
    imagePath: string;
    linkHref: string | undefined;
    publishDate: string;
    readMoreLabel?: string;
    surtitle?: string;
    summary: string;
    title: string;
}

export interface BlogCategoryFeaturedArticleStyles {
    wrapper?: InjectableCss;
    tileClickable?: ClickablePresentationProps;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    content?: InjectableCss;
    header?: InjectableCss;
    surtitle?: TypographyPresentationProps;
    titleClickable?: ClickablePresentationProps;
    title?: TypographyPresentationProps;
    publishDate?: InjectableCss;
    summary?: TypographyPresentationProps;
    footer?: InjectableCss;
    readMore?: TypographyPresentationProps;
}

export const blogCategoryFeaturedArticleStyles: BlogCategoryFeaturedArticleStyles = {
    wrapper: {
        css: css`
            position: relative;
            padding: ${pxToRem(24, 0)};
            border-width: 1px 0;
            border-color: ${getColor("common.borderDark")};
            border-style: solid;
        `,
    },
    tileClickable: mergeProps<BlogCategoryFeaturedArticleStyles["tileClickable"]>(blogProps.tileClickable, {
        css: css`
            &:hover {
                ~ div section .readMore {
                    color: ${getColor("text.linkHover")};
                    text-decoration: underline;
                }
            }
        `,
    }),
    gridContainer: { gap: 24 },
    gridItem: { width: [12, 12, 6, 6, 6] },
    imageWrapper: blogProps.imageWrapper,
    image: {
        css: css`
            ${x => x.theme.lazyImage.defaultProps?.css}
            display: flex;

            img {
                ${blogTileImageStyles()}
            }
        `,
    },
    content: {
        css: css`
            display: flex;
            height: 100%;
            flex-direction: column;
            justify-content: center;

            ${breakpoint("lg")} {
                flex-basis: 80%;
            }
        `,
    },
    surtitle: blogProps.surtitle,
    titleClickable: {
        css: css`
            ${linkAltStyles()}
            ${spacingStyles(pxToRem(6), 0)}
        `,
    },
    title: {
        variant: "h4",
        forwardAs: "h2",
    },
    publishDate: mergeProps<BlogCategoryFeaturedArticleStyles["publishDate"]>(blogProps.publishDate, {
        css: css`
            ${spacingStyles(pxToRem(8), 0)}
        `,
    }),
    summary: {
        css: css`
            ${spacingStyles(pxToRem(24), 0)}
            color: ${getColor("text.accent")};
        `,
    },
    footer: {
        css: css`
            ${spacingStyles(pxToRem(16))}
        `,
    },
    readMore: {
        css: css`
            ${blogTileReadMoreStyles("text.link")}
        `,
    },
};

const BlogCategoryFeaturedArticle: FC<BlogCategoryFeaturedArticleProps> = ({
    extendedStyles,
    overridedStyles,
    imagePath,
    linkHref,
    publishDate,
    readMoreLabel,
    summary: baseSummary,
    surtitle,
    title: baseTitle,
    ...htmlAttributes
}) => {
    const styles = useExpandProps(blogCategoryFeaturedArticleStyles, extendedStyles, overridedStyles);
    const language = useCurrentLanguage();

    if (!linkHref) {
        return null;
    }

    const title = truncate(baseTitle, { length: 180 });
    const summary = truncate(baseSummary, { length: 350 });

    const publishDateDisplay = getLocalizedDateTime({
        dateTime: new Date(publishDate),
        language,
        options: constants.blog.getLocalizedDateTimeOptions,
    });

    return (
        <StyledWrapper {...styles.wrapper} as="article" {...htmlAttributes}>
            <Clickable {...styles.tileClickable} href={linkHref} tabIndex={-1} aria-hidden="true">
                {translate("{0} {1} Publish {2}", surtitle || "", title, publishDateDisplay)}
            </Clickable>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.gridItem}>
                    <StyledWrapper {...styles.imageWrapper} aria-hidden="true">
                        <LazyImage {...styles.image} src={imagePath} altText="" />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.gridItem}>
                    <StyledWrapper {...styles.content} as="section">
                        <StyledWrapper {...styles.header} as="header">
                            {surtitle ? (
                                <Typography {...styles.surtitle} aria-hidden="true">
                                    {surtitle}
                                </Typography>
                            ) : null}
                            <Clickable
                                {...styles.titleClickable}
                                href={linkHref}
                                aria-label={translate("{0} {1} Publish {2}", surtitle || "", title, publishDateDisplay)}
                            >
                                <Typography {...styles.title}>{title}</Typography>
                            </Clickable>
                            <StyledWrapper
                                {...styles.publishDate}
                                as="time"
                                dateTime={publishDate.toString()}
                                aria-hidden="true"
                            >
                                {publishDateDisplay}
                            </StyledWrapper>
                        </StyledWrapper>
                        <Typography {...styles.summary} aria-hidden="true">
                            {parse(summary, parserOptions)}
                        </Typography>
                        {readMoreLabel ? (
                            <StyledWrapper {...styles.footer} as="footer" aria-hidden="true">
                                <Typography {...styles.readMore} className="readMore">
                                    {readMoreLabel}
                                </Typography>
                            </StyledWrapper>
                        ) : null}
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

export default BlogCategoryFeaturedArticle;
